import { Controller } from "@hotwired/stimulus"

// Reusable searchable popover for filter chips.
export default class extends Controller {
  static targets = ["trigger", "menu", "search", "item", "empty"]

  connect() {
    this.boundClickOutside = this.handleClickOutside.bind(this)
    this.boundKeydown = this.handleKeydown.bind(this)
    this.boundCloseForOtherMenu = this.closeForOtherMenu.bind(this)
    window.addEventListener("filter-dropdown:open", this.boundCloseForOtherMenu)
    this.setExpanded(false)
  }

  disconnect() {
    document.removeEventListener("click", this.boundClickOutside)
    document.removeEventListener("keydown", this.boundKeydown)
    window.removeEventListener("filter-dropdown:open", this.boundCloseForOtherMenu)
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    if (!this.hasMenuTarget) return

    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    window.dispatchEvent(new CustomEvent("filter-dropdown:open", { detail: { controller: this } }))
    this.menuTarget.hidden = false
    this.setExpanded(true)
    this.filter()
    document.addEventListener("click", this.boundClickOutside)
    document.addEventListener("keydown", this.boundKeydown)

    if (this.hasSearchTarget) {
      requestAnimationFrame(() => this.searchTarget.focus())
    }
  }

  close() {
    if (this.hasMenuTarget) this.menuTarget.hidden = true
    this.setExpanded(false)
    this.clearSearch()
    document.removeEventListener("click", this.boundClickOutside)
    document.removeEventListener("keydown", this.boundKeydown)
  }

  filter() {
    if (!this.hasItemTarget) return

    const query = this.hasSearchTarget ? this.searchTarget.value.trim().toLowerCase() : ""
    let visibleCount = 0

    this.itemTargets.forEach((item) => {
      const matches = item.textContent.trim().toLowerCase().includes(query)
      item.hidden = !matches
      if (matches) visibleCount += 1
    })

    if (this.hasEmptyTarget) {
      this.emptyTarget.hidden = visibleCount > 0
    }
  }

  navigate(event) {
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return

    const items = this.visibleItems()
    if (items.length === 0) return

    event.preventDefault()
    const currentIndex = items.indexOf(document.activeElement)
    let nextIndex = 0

    if (event.key === "ArrowDown") {
      nextIndex = currentIndex >= 0 ? (currentIndex + 1) % items.length : 0
    } else if (event.key === "ArrowUp") {
      nextIndex = currentIndex >= 0 ? (currentIndex - 1 + items.length) % items.length : items.length - 1
    } else if (event.key === "End") {
      nextIndex = items.length - 1
    }

    items[nextIndex].focus()
  }

  handleClickOutside(event) {
    if (this.element.contains(event.target)) return
    this.close()
  }

  handleKeydown(event) {
    if (event.key === "Escape") this.close()
  }

  closeForOtherMenu(event) {
    if (event.detail.controller === this) return
    this.close()
  }

  get isOpen() {
    return this.hasMenuTarget && !this.menuTarget.hidden
  }

  visibleItems() {
    return this.itemTargets.filter((item) => !item.hidden)
  }

  setExpanded(expanded) {
    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute("aria-expanded", expanded ? "true" : "false")
    }
  }

  clearSearch() {
    if (!this.hasSearchTarget || this.searchTarget.value === "") return

    this.searchTarget.value = ""
    this.filter()
  }
}
